import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faUpload, faEye, faTrash, faCheck, faXmark } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import {
  authFetch,
  formatDate,
  openAuthedFile,
  DOCUMENT_TYPE_LABELS,
  DOCUMENT_DISPLAY_ORDER,
  DOCUMENT_STATUS_BADGE,
  DOCUMENT_STATUS_LABELS,
} from '../../utils/http';

export interface AdminDoc {
  id: string;
  documentType: number;
  fileName: string;
  fileSizeBytes: number;
  status: number;
  reviewNote: string | null;
  uploadedAt: string;
}

interface Props {
  studentRecordId: string;
  documents: AdminDoc[];
  onChanged: () => void;
}

/** Admin view of one learner's four documents: view, upload/replace, verify/reject, delete. */
export default function AdminStudentDocumentsEditor({ studentRecordId, documents, onChanged }: Props) {
  const { accessToken } = useAuth();
  const [error, setError] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});

  const view = async (id: string) => {
    const err = await openAuthedFile(accessToken, `/api/student-documents/${id}/file`);
    if (err) setError(err);
  };

  const upload = async (type: number, file: File) => {
    setError('');
    setBusy(`type-${type}`);
    const form = new FormData();
    form.append('file', file);
    const res = await authFetch(accessToken, `/api/student-documents/student/${studentRecordId}/${type}`, { method: 'POST', form });
    setBusy(null);
    if (!res.ok) setError(res.message);
    else onChanged();
  };

  const review = async (id: string, verify: boolean) => {
    setError('');
    setBusy(id);
    const res = await authFetch(accessToken, `/api/student-documents/${id}/review`, {
      method: 'POST',
      body: { verify, note: notes[id] ?? '' },
    });
    setBusy(null);
    if (!res.ok) setError(res.message);
    else onChanged();
  };

  const remove = async (id: string) => {
    if (!window.confirm('Delete this document? The learner will need to upload it again.')) return;
    setBusy(id);
    const res = await authFetch(accessToken, `/api/student-documents/${id}`, { method: 'DELETE' });
    setBusy(null);
    if (!res.ok) setError(res.message);
    else onChanged();
  };

  return (
    <div className="space-y-3">
      {error && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">{error}</p>}

      {DOCUMENT_DISPLAY_ORDER.map((type) => {
        const doc = documents.find((d) => d.documentType === type);
        const uploading = busy === `type-${type}`;
        return (
          <div key={type} className="border border-canvas-300 rounded-2xl p-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-brand-950">{DOCUMENT_TYPE_LABELS[type]}</p>
                {doc ? (
                  <p className="text-xs text-brand-900/50">
                    {doc.fileName} · uploaded {formatDate(doc.uploadedAt)}
                  </p>
                ) : (
                  <p className="text-xs text-brand-900/50">Not uploaded</p>
                )}
                {doc?.reviewNote && <p className="text-xs text-brand-900/70 mt-1">Note: {doc.reviewNote}</p>}
              </div>
              <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${doc ? DOCUMENT_STATUS_BADGE[doc.status] : 'bg-gray-100 text-gray-600'}`}>
                {doc ? DOCUMENT_STATUS_LABELS[doc.status] : 'Missing'}
              </span>
            </div>

            <div className="flex flex-wrap gap-2 mt-3">
              {doc && (
                <button
                  onClick={() => view(doc.id)}
                  className="px-3 py-1.5 border border-canvas-300 text-brand-900 text-xs font-semibold rounded-lg hover:bg-canvas-100 transition-colors flex items-center gap-1.5"
                >
                  <FontAwesomeIcon icon={faEye} className="h-3 w-3" />
                  View
                </button>
              )}
              <label className="px-3 py-1.5 border border-canvas-300 text-brand-900 text-xs font-semibold rounded-lg hover:bg-canvas-100 transition-colors cursor-pointer flex items-center gap-1.5">
                <FontAwesomeIcon icon={uploading ? faSpinner : faUpload} className={`h-3 w-3 ${uploading ? 'animate-spin' : ''}`} />
                {doc ? 'Replace' : 'Upload'}
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  className="hidden"
                  disabled={uploading}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = '';
                    if (f) upload(type, f);
                  }}
                />
              </label>
              {doc && (
                <>
                  <input
                    type="text"
                    value={notes[doc.id] ?? ''}
                    onChange={(e) => setNotes((prev) => ({ ...prev, [doc.id]: e.target.value }))}
                    placeholder="Note (required to reject)"
                    className="flex-1 min-w-[140px] px-3 py-1.5 border border-canvas-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-brand-500"
                  />
                  <button
                    onClick={() => review(doc.id, true)}
                    disabled={busy === doc.id}
                    className="px-3 py-1.5 bg-brand-gradient text-white text-xs font-semibold rounded-lg hover:shadow-lg transition-all disabled:opacity-50 flex items-center gap-1.5"
                  >
                    <FontAwesomeIcon icon={faCheck} className="h-3 w-3" />
                    Verify
                  </button>
                  <button
                    onClick={() => review(doc.id, false)}
                    disabled={busy === doc.id}
                    className="px-3 py-1.5 border border-red-200 text-red-600 text-xs font-semibold rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50 flex items-center gap-1.5"
                  >
                    <FontAwesomeIcon icon={faXmark} className="h-3 w-3" />
                    Reject
                  </button>
                  <button onClick={() => remove(doc.id)} disabled={busy === doc.id} className="p-2 text-red-500 hover:bg-red-50 rounded-lg" title="Delete">
                    <FontAwesomeIcon icon={faTrash} className="h-3.5 w-3.5" />
                  </button>
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
